
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageDialog } from "../MessageDialog";
import { PurchaseRequestDialog } from "./PurchaseRequestDialog";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { 
  Star, 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle,
  ShoppingCart,
  Award,
  Timer,
  Zap
} from "lucide-react";

interface SellerInfo {
  id: string;
  full_name?: string;
  profile_photo?: string;
  rating?: number;
  total_reviews?: number;
  response_rate?: number;
  response_time?: number;
  about_me?: string;
  experience?: string;
  education?: string;
  languages?: string[];
  certifications?: string[];
}

interface Product {
  id: string;
  title: string;
  price: number;
  currency?: string;
  condition?: string;
  address?: string;
  phone?: string;
  professional_id: string;
}

interface SellerInfoSectionProps {
  seller: SellerInfo;
  product: Product;
  onPurchaseRequest: () => void;
}

export const SellerInfoSection = ({ 
  seller, 
  product, 
  onPurchaseRequest 
}: SellerInfoSectionProps) => {
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const { session } = useAuth();

  const handleMessageClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour envoyer un message");
      return;
    }

    if (!product.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening message dialog for professional:", product.professional_id);
    setIsMessageDialogOpen(true);
  };

  const handlePurchaseClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour faire une demande d'achat");
      return;
    }

    if (!product.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening purchase dialog for product:", product.id);
    setIsPurchaseDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Informations sur le vendeur */}
      <Card>
        <CardHeader>
          <CardTitle>Vendeur</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage 
                src={seller.profile_photo} 
                alt={seller.full_name || "Vendeur"} 
              />
              <AvatarFallback>
                {seller.full_name?.charAt(0) || "V"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">
                {seller.full_name || "Vendeur"}
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                {seller.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>{seller.rating}/5</span>
                    {seller.total_reviews && (
                      <span>({seller.total_reviews} avis)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statistiques du vendeur */}
          <div className="grid grid-cols-2 gap-4">
            {seller.response_rate && (
              <div className="flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>{seller.response_rate}% de réponse</span>
              </div>
            )}
            {seller.response_time && (
              <div className="flex items-center gap-2 text-sm">
                <Timer className="h-4 w-4 text-green-500" />
                <span>Répond en {seller.response_time}h</span>
              </div>
            )}
          </div>

          {/* À propos du vendeur */}
          {seller.about_me && (
            <div className="pt-4 border-t">
              <h4 className="font-medium text-sm text-gray-700 mb-2">À propos</h4>
              <p className="text-sm text-gray-600">{seller.about_me}</p>
            </div>
          )}

          {/* Expérience et formation */}
          <div className="grid grid-cols-1 gap-4 pt-4 border-t">
            {seller.experience && (
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Expérience
                </h4>
                <p className="text-sm text-gray-600">{seller.experience}</p>
              </div>
            )}
            {seller.education && (
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1">Formation</h4>
                <p className="text-sm text-gray-600">{seller.education}</p>
              </div>
            )}
          </div>

          {/* Langues et certifications */}
          {(seller.languages || seller.certifications) && (
            <div className="pt-4 border-t space-y-3">
              {seller.languages && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Langues</h4>
                  <div className="flex flex-wrap gap-1">
                    {seller.languages.map((language, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {seller.certifications && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Certifications</h4>
                  <div className="flex flex-wrap gap-1">
                    {seller.certifications.map((cert, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informations sur le produit */}
      <Card>
        <CardHeader>
          <CardTitle>Informations pratiques</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">Prix:</span>
            <span className="text-lg font-bold text-green-600">
              {product.price}€
            </span>
          </div>
          
          {product.condition && (
            <div className="flex items-center justify-between">
              <span className="font-medium">État:</span>
              <Badge variant="outline">{product.condition}</Badge>
            </div>
          )}

          {product.address && (
            <div className="flex items-start gap-3 pt-2">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <span className="text-gray-700">{product.address}</span>
            </div>
          )}
          
          {product.phone && (
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <span className="text-gray-700">{product.phone}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Boutons d'action */}
      <div className="space-y-3">
        <Button 
          onClick={handlePurchaseClick}
          size="lg"
          className="w-full"
        >
          <ShoppingCart className="h-5 w-5 mr-2" />
          Demander l'achat
        </Button>
        
        <Button 
          variant="outline" 
          onClick={handleMessageClick}
          size="lg"
          className="w-full"
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          Contacter le vendeur
        </Button>
      </div>

      {/* Dialog de message */}
      <MessageDialog
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
        professionalId={product.professional_id}
        professionalName={seller.full_name}
        context="product"
        itemTitle={product.title}
      />

      {/* Dialog de demande d'achat */}
      <PurchaseRequestDialog
        open={isPurchaseDialogOpen}
        onOpenChange={setIsPurchaseDialogOpen}
        productId={product.id}
        professionalId={product.professional_id}
        productTitle={product.title}
        productPrice={product.price}
      />
    </div>
  );
};
